import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import {scaleLinear, scaleBand} from 'd3-scale';
import {select} from 'd3-selection';
import * as d3Axis from 'd3-axis';
import moment from 'moment';

import {Popover} from '@albert-io/atomic';

import AuthorDashboardMetricsQuestionDifficulties from './AuthorDashboardMetricsQuestionsDifficulties.react';
import './author-dashboard-metrics.scss';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const HEIGHT = 100;
const PADDING = 30;

const CURR_MONTH = moment().format('MMM');

export class AuthorDashboardBarChart extends React.Component {
  static propTypes = {
    chartData: PropTypes.object
  };

  constructor() {
    super();

    this.state = {
      hasHover: false,
      width: '500',
      month: null,
      position: null,
      parentWidth: null
    };

    this.barChart = React.createRef();
  }

  componentDidMount() {
    this.initSize();
  }

  componentDidUpdate() {
    if (this.barChart) {
      const parentWidth = this.barChart.current.getBoundingClientRect().width;
      if (this.state.parentWidth !== parentWidth) {
        // eslint-disable-next-line
        this.setState({parentWidth});
      }
    }
  }

  initSize = () => {
    const {current} = this.barChart;
    const parentWidth = current.getBoundingClientRect().width;
    this.setState({parentWidth});
  };

  handleHover = (e, hasHover) => {
    if (hasHover) {
      const {chartData} = this.props;
      const month = e.target.getAttribute('name');
      const position = e.target.getAttribute('x');
      this.setState({
        month: chartData[month],
        position
      });
    }
    this.setState({hasHover});
  };

  addMonths = (chartData) => {
    MONTHS.forEach((month) => {
      if (!chartData[month]) {
        chartData[month] = {
          count: 0,
          easy: 0,
          difficult: 0,
          moderate: 0
        };
      }
    });

    return chartData;
  };

  render() {
    const chartData = this.addMonths(this.props.chartData);
    const WIDTH = this.state.width;
    const maxValue = Math.max(...Object.values(chartData).map((m) => m.count));

    // this is to calculate the difference of width between the outer parent contianer and
    // the width of the graoh, so we know where to place the popover
    // _____________________
    // |    |         |    |
    // |    |<-chart->|    |<- parent container
    // |____|_________|____|
    //           ______
    //  <-left-->|    | <- popover
    //           |____|

    const widthDifference = Math.floor(
      this.state.position - 120 + (this.state.parentWidth - WIDTH) / 2
    );
    const xScale = scaleBand()
      .padding(0.8)
      .range([0, WIDTH])
      .domain(MONTHS);

    const yScale = scaleLinear()
      .domain([-1, maxValue])
      .range([HEIGHT, 0]);

    return (
      <div className='author-dashborad-metric__bar-chart--container' ref={this.barChart}>
        <svg width={WIDTH} height={HEIGHT + PADDING}>
          <Axis orientation='Bottom' scale={xScale} />

          <g>
            {Object.entries(chartData).map(([date, values], idx) => {
              return (
                <rect
                  className={classnames(`author-dashboard-metric__bar-chart`, {
                    'author-dashboard-metric__bar-chart--hovered': this.state.hasHover,
                    'author-dashboard-metric__bar-chart-empty': values.count === 0
                  })}
                  key={idx}
                  name={date}
                  x={xScale(date)}
                  y={values.count === 0 ? HEIGHT - 10 : yScale(values.count)}
                  height={values.count === 0 ? 10 : HEIGHT - yScale(values.count)}
                  width='8px'
                  rx='6'
                  onMouseEnter={(e) => this.handleHover(e, true)}
                  onMouseLeave={(e) => this.handleHover(e, false)}
                />
              );
            })}
          </g>
        </svg>
        {this.state.hasHover && (
          <Popover
            className='author-dashboard-metrics__popover'
            expanded
            position='bottom-start'
            targetRef={this.barChart}
            style={{left: `${widthDifference}px`}}
          >
            <AuthorDashboardMetricsQuestionDifficulties questionDifficulties={this.state.month} />
          </Popover>
        )}
      </div>
    );
  }
}

class Axis extends React.Component {
  static propTypes = {
    orientation: PropTypes.string,
    scale: PropTypes.func
  };

  constructor() {
    super();
    this.axisRef = React.createRef();
  }

  componentDidMount() {
    this.renderAxis();
  }

  renderAxis = () => {
    const axisType = this.props.orientation === 'Left' ? 'axisLeft' : 'axisBottom';
    const axis = d3Axis[axisType]().scale(this.props.scale);

    select(this.axisRef.current)
      .call(axis)
      .call((g) =>
        g.selectAll('.tick text')._groups[0].forEach((item) => {
          if (item.innerHTML === CURR_MONTH) {
            item.setAttribute('class', 'author-dashboard-metrics__chart-axis--Bottom--current');
          }
        })
      )
      .attr('transform', `translate(0 ${HEIGHT})`);
  };

  render() {
    const {orientation} = this.props;
    return (
      <g
        ref={this.axisRef}
        className={`author-dashboard-metrics__chart-axis author-dashboard-metrics__chart-axis--${orientation}`}
      />
    );
  }
}
